import React, { useState, useEffect } from 'react'
import { Table, Tag, Card, message } from 'antd'
import { apiClient } from '../../../shared/api/axios-instance'

interface MerchantReferralHistoryProps {
    merchantId: number
}

interface ReferralRecord {
    id: number
    referred_merchant_id: number
    referred_business_name: string
    campaign_name: string | null
    status: string
    created_at: string
}

const STATUS_COLORS: Record<string, string> = {
    INVITED: 'default',
    SIGNED_UP: 'warning',
    PROFILE_COMPLETED: 'processing',
    FIRST_RFQ: 'purple',
    FIRST_ORDER: 'success',
}

export const MerchantReferralHistory: React.FC<MerchantReferralHistoryProps> = ({ merchantId }) => {
    const [referrals, setReferrals] = useState<ReferralRecord[]>([])
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        const fetchReferrals = async () => {
            try {
                setIsLoading(true)
                // Note: We might need an endpoint in the merchant service like `/admin/merchants/{id}/referrals`
                // But for MVP, we'll try to fetch from an assumed endpoint, or just show empty state if not available
                const response = await apiClient.get(`/admin/merchants/${merchantId}/referrals`)
                setReferrals(response.data.data || [])
            } catch (error: any) {
                // If endpoint doesn't exist yet, we just swallow the error for now to not break the UI
                if (error.response?.status !== 404) {
                    message.error('Failed to load referral history')
                }
            } finally {
                setIsLoading(false)
            }
        }
        fetchReferrals()
    }, [merchantId])

    const columns = [
        {
            title: 'Referred Business',
            dataIndex: 'referred_business_name',
            key: 'referred_business_name',
            render: (text: string) => text || 'Unknown',
        },
        {
            title: 'Campaign',
            dataIndex: 'campaign_name',
            key: 'campaign_name',
            render: (text: string) => text || '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={STATUS_COLORS[status] || 'default'}>
                    {status?.replace(/_/g, ' ')}
                </Tag>
            ),
        },
        {
            title: 'Referred On',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: string) => new Date(date).toLocaleDateString(),
        },
    ]

    return (
        <Card size="small" bordered={false}>
            <Table
                columns={columns}
                dataSource={referrals}
                loading={isLoading}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: 'No referrals made by this merchant yet.' }}
            />
        </Card>
    )
}
